import React, { useMemo } from 'react';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { FiPlus } from 'react-icons/fi';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { SectionModule } from '../../../../api/models/BibleStudyModule';
import { CreateBibleStudySection } from '../../../../api/models/BibleStudySection';
import { reorder } from '../../../../util/reorder';
import { HorizontalContent } from '../../../core/HorizontalContent';
import { RhButton } from '../../../core/RhButton';
import { RhCard } from '../../../core/RhCard';
import { H2 } from '../../../core/Styles';
import { ModuleFormState } from '../../bible-study-form';
import {
  getModuleGroupKey,
  getModuleGroupsLibrary,
  getModuleTagsBySection,
  getSectionTags,
} from '../../bible-study-form.helper';
import { StudySectionCard } from './StudySectionCard';
import { StudySectionDayCard } from './StudySectionDayCard';
import { StudySectionModuleCards } from './StudySectionModuleCards';

type Props = {
  /** sections of the current program */
  sections: CreateBibleStudySection[];
  /** form for the section module */
  moduleForm: ModuleFormState;
  /** set "Add +" status to loading & disabled */
  isLoading: boolean;
  /** if current form should be on edit mode */
  isEditMode: boolean;
  /** user wants to add content via jira-like modal */
  userClickedAddViaModal: () => void;
  /** called when the user edits the module fields */
  userUpdatedModuleForm: (formState: ModuleFormState) => void;
  /** open or close module modal */
  userToggledModuleModal: () => void;
  /** user wants to update sections array */
  userUpdatedSections: (sections: CreateBibleStudySection[]) => void;
  /** handles the edit module button onclick event */
  userClickedEditModule?: (weekTag: string, dayTag: string, moduleIndex: number) => void;
  /** handles the delete module button onclick event */
  userClickedDeleteModule?: (weekTag: string, dayTag: string, moduleIndex: number) => void;
  /** handles the delete section button onclick event */
  userClickedDeleteSection?: (sectionIndex: number) => void;
};

/* -------------------------------------------------------------------------- */
/* START::MainComponent                                                       */
/* -------------------------------------------------------------------------- */

/**
 * List of Bible Study Section that is drag-n-drop ready
 * @param props
 * @returns
 */
export const StudySectionCards = (props: Props) => {
  const onDragEnd = (result: DropResult) => {
    const { destination, source, type } = result;

    // if there is no destination, do nothing
    if (!destination) {
      return;
    }

    const sourceSectionId = source.droppableId;
    const destSectionId = destination.droppableId;

    // if location of draggable didn't change, do nothing
    if (sourceSectionId === destSectionId && source.index === destination.index) {
      return;
    }

    if (type === 'droppableItem') {
      // reordering sections
      const newSections = reorder(props.sections, source.index, destination.index);
      props.userUpdatedSections(newSections);
    } else if (type === 'droppableSubItem') {
      // reordering modules

      // a library of items and sub-items
      // e.g. { "Section 1": [{...module}] }
      const sectionLibrary = props.sections.reduce((acc: { [key: string]: SectionModule[] }, section) => {
        acc[section.name] = section.modules;
        return acc;
      }, {});

      const sourceModules = sectionLibrary[sourceSectionId];
      const destModules = sectionLibrary[destSectionId];

      if (sourceSectionId === destSectionId) {
        // module was re-ordered inside the same section
        const newModules = reorder(sourceModules, source.index, destination.index);
        const newSections = props.sections.map(section => {
          if (section.name === sourceSectionId) {
            return { ...section, modules: newModules };
          } else {
            return section;
          }
        });
        props.userUpdatedSections(newSections);
      } else {
        // module was re-ordered in different section
        let newSourceModules = [...sourceModules];
        let newDestModules = [...destModules];

        const [draggedModule] = newSourceModules.splice(source.index, 1);
        newDestModules.splice(destination.index, 0, draggedModule);

        const newSections = props.sections.map(section => {
          if (section.name === sourceSectionId) {
            return { ...section, modules: newSourceModules };
          } else if (section.name === destSectionId) {
            return { ...section, modules: newDestModules };
          }
          return section;
        });

        props.userUpdatedSections(newSections);
      }
    }
  };

  const moduleLibrary = useMemo(() => getModuleGroupsLibrary(props.sections), [props.sections]);
  const weekTags = useMemo(() => getSectionTags(props.sections), [props.sections]);

  return (
    <RhCard mode="tiled">
      <HorizontalContent
        left={<H2>Sections</H2>}
        right={[
          <RhButton
            key="add-content-via-modal"
            title="Add Content"
            loading={props.isLoading}
            disabled={props.isLoading}
            suffixIcon={FiPlus}
            onClick={props.userClickedAddViaModal}
          />,
        ]}
      />
      {!!props.sections.length && (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable" type="droppableItem">
            {droppableProvided => (
              <StudySectionCardsDiv ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
                {props.sections.map((section, sectionIndex) => {
                  const moduleTags = getModuleTagsBySection(section);

                  return (
                    <Draggable key={section.name} draggableId={section.name} index={sectionIndex}>
                      {draggableProvided => (
                        <div
                          ref={draggableProvided.innerRef}
                          key={`Section ${sectionIndex}`}
                          {...draggableProvided.draggableProps}>
                          <StudySectionCard
                            dragHandleProps={draggableProvided.dragHandleProps}
                            title={section.name}
                            isEditMode={props.isEditMode}
                            onChangeTitle={(value, { oldValue, resetValue }) => {
                              const newValue = value.trim();

                              if (oldValue.toLowerCase() === newValue.toLowerCase()) {
                                // Just do nothing :)
                                return;
                              }

                              const isDuplicateTagFound =
                                weekTags.findIndex(tag => tag.toLowerCase() === newValue.toLowerCase()) !== -1;

                              if (isDuplicateTagFound) {
                                toast.info(`${newValue} already exists!`);
                                resetValue();
                                return;
                              }

                              const newSections = [...props.sections];
                              newSections.splice(sectionIndex, 1, {
                                ...newSections[sectionIndex],
                                name: newValue,
                                tag: newValue,
                              });

                              props.userUpdatedSections(newSections);
                              toast.success(`Renamed to ${newValue}`);
                            }}
                            onDeleteButtonClick={
                              // delete button wont be displayed if
                              // [userClickedDeleteSection] was not passed
                              !!props.userClickedDeleteSection
                                ? () => props.userClickedDeleteSection?.(sectionIndex)
                                : undefined
                            }>
                            {moduleTags.map(moduleTag => {
                              const sectionTag = section.tag || section.name;
                              const key = getModuleGroupKey(sectionTag, moduleTag);
                              const modules = moduleLibrary[key];

                              if (!modules?.length) return null;
                              return (
                                <StudySectionDayCard key={moduleTag} title={moduleTag}>
                                  <StudySectionModuleCards
                                    id={section.name}
                                    modules={modules}
                                    {...(props.userClickedEditModule && {
                                      userClickedEditModule: moduleDayIndex =>
                                        props.userClickedEditModule?.(sectionTag, moduleTag, moduleDayIndex),
                                    })}
                                    {...(props.userClickedDeleteModule && {
                                      userClickedDeleteModule: moduleDayIndex =>
                                        props.userClickedDeleteModule?.(sectionTag, moduleTag, moduleDayIndex),
                                    })}
                                    userReOrderedModule={(module, dir) => {
                                      // Get the section index of which this module belongs to so that we know where to insert it later
                                      const sectionIdx = props.sections.findIndex(
                                        s => s.modules.findIndex(m => module.moduleId === m.moduleId) !== -1
                                      );
                                      const section = props.sections[sectionIdx];

                                      // Get all modules of the week this module belongs to that we're gonna re-order later
                                      const modulesOfWeek = Object.entries(moduleLibrary)
                                        .reduce<SectionModule[]>((acum, [key, entry]) => {
                                          const currentTag = key.split('_')[0];
                                          const isSameSection = currentTag.toLowerCase() === section.tag!.toLowerCase();
                                          return isSameSection ? acum.concat(entry) : acum;
                                        }, [])
                                        .map((m, i) => ({ ...m, sortOrder: i + 1 }));

                                      // Disable re-order if this module is on first or last position of the day
                                      const moduleIWithinDay = modules.findIndex(m => m.moduleId === module.moduleId);
                                      const isFirst = moduleIWithinDay === 0;
                                      const isLast = moduleIWithinDay === modules.length - 1;
                                      if ((isFirst && dir === 'up') || (isLast && dir === 'down')) {
                                        return;
                                      }

                                      // Re-order modules
                                      const moduleIWithinWeek = modulesOfWeek.findIndex(
                                        m => m.moduleId === module.moduleId
                                      );
                                      const destinationI = dir === 'up' ? moduleIWithinWeek - 1 : moduleIWithinWeek + 1;
                                      const newModules: SectionModule[] = reorder(
                                        modulesOfWeek,
                                        moduleIWithinWeek,
                                        destinationI
                                      ).map((m, i) => ({ ...m, sortOrder: i + 1 }));

                                      // Insert re-ordered modules to the section it belongs
                                      const newSections = [...props.sections];
                                      newSections.splice(sectionIdx, 1, { ...section, modules: newModules });
                                      props.userUpdatedSections(newSections);
                                    }}
                                  />
                                </StudySectionDayCard>
                              );
                            })}
                          </StudySectionCard>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {droppableProvided.placeholder}
              </StudySectionCardsDiv>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </RhCard>
  );
};

export const StudySectionCardsDiv = styled.div`
  margin-top: 18.5px;
  & > :not(:last-child) {
    margin-bottom: 20.5px;
  }
`;
